import { useEffect, useRef, useState } from 'react';
import './AboutUs.scss';
import { getLanguage } from '../../utils/Helper';

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionsRef = useRef([]);
  const lang = getLanguage();

  const content = {
    EN: {
      hero: {
        title: "About",
        subtitle: "Addmix Store",
        tagline: "Luxury You Deserve",
        description: "Established on August 15th, 2024 with one clear purpose: to deliver real luxury to people who truly deserve it.",
        since: "Since 2024"
      },
      intro: {
        title: "Who We Are",
        text: "We are not just a store that sells products — we are a complete shopping experience built on clarity, quality, speed, and respect for our customers. Addmix Store is one of the leading Luxury Multicategory Stores in Egypt and the Arab region."
      },
      vision: {
        title: "Our Vision",
        text: "To reshape the meaning of luxury in the Arab world — making it smart, easy, and accessible without compromising quality or value."
      },
      mission: {
        title: "Our Mission",
        text: "To provide every customer with an experience that understands their needs, respects their time, and delivers the level of quality they truly deserve."
      },
      values: {
        title: "Our Values",
        items: [
          { icon: "💎", title: "Quality Above Everything", desc: "Premium products you can trust" },
          { icon: "✨", title: "Luxury You Deserve", desc: "Real elegance within reach" },
          { icon: "⚡", title: "Fast Service", desc: "Speed and respect in every interaction" },
          { icon: "🎯", title: "Wide Variety", desc: "Something for every taste" },
          { icon: "💰", title: "Clear Pricing", desc: "Competitive and transparent" },
          { icon: "🤝", title: "Trust-Based Support", desc: "After-sales care you can rely on" }
        ]
      },
      why: {
        title: "Why Addmix Store?",
        reasons: [
          {
            title: "Deep Experience",
            text: "We are importers, wholesalers, and retailers with years of expertise in sourcing and quality selection."
          },
          {
            title: "Dual Experience",
            text: "Our physical store delivers a refined premium shopping experience, while our online platform offers smooth, fast, and reliable service."
          },
          {
            title: "Full Transparency",
            text: "We are honest and clear with every product grade we offer — no hidden details, no confusion."
          }
        ]
      },
      promise: {
        title: "Our Promise",
        text: "Your experience comes first. Real quality, trusted service, and a shopping journey that keeps you coming back."
      }
    },
    AR: {
      hero: {
        title: "من نحن",
        subtitle: "أددميكس ستور",
        tagline: "فخامة تستحقها",
        description: "بدأنا يوم 15 أغسطس 2024 بفكرة واضحة وبسيطة: أن كل واحد يستحق ياخد فخامة حقيقية تناسب ذوقه ومستواه من غير تعقيد ومن غير مبالغة.",
        since: "منذ 2024"
      },
      intro: {
        title: "احنا مين",
        text: "احنا مش مجرد متجر بيبيع منتجات — احنا مكان بيقدم تجربة شراء مختلفة، تجربة محترمة، واضحة، سريعة، وفيها اهتمام بكل تفصيلة بتلمس عميلنا. أددميكس يعتبر واحد من أقوى متاجر الفخامة متعددة الفئات في مصر والوطن العربي."
      },
      vision: {
        title: "رؤيتنا",
        text: "نقدم مفهوم جديد للفخامة في العالم العربي — فخامة ذكية، سهلة، وفي المتناول من غير ما نقلل من الجودة ومن غير ما نبالغ في السعر."
      },
      mission: {
        title: "رسالتنا",
        text: "أن أي عميل يدخل أددميكس يحس إنه في مكان فاهم احتياجاته، وبيقدم له خدمة تستاهل ثقته، وجودة تناسب قيمته، وتجربة بسيطة وسريعة ومن غير لف ودوران."
      },
      values: {
        title: "قيمنا",
        items: [
          { icon: "💎", title: "الجودة رقم واحد", desc: "منتجات فاخرة تستحق ثقتك" },
          { icon: "✨", title: "فخامة تستحقها", desc: "أناقة حقيقية في متناول يدك" },
          { icon: "⚡", title: "خدمة سريعة", desc: "سرعة واحترام في كل تعامل" },
          { icon: "🎯", title: "تنوع كبير", desc: "حاجة تناسب كل الأذواق" },
          { icon: "💰", title: "أسعار واضحة", desc: "تنافسية وشفافة" },
          { icon: "🤝", title: "دعم بعد البيع", desc: "خدمة مبنية على الثقة" }
        ]
      },
      why: {
        title: "ليه أددميكس؟",
        reasons: [
          {
            title: "خبرة عميقة",
            text: "لأننا مستوردين وتجار جملة وقطاعي وده بيدينا خبرة كبيرة في اختيار المنتج الأفضل وفي فهم العميل بشكل أدق."
          },
          {
            title: "تجربة مزدوجة",
            text: "لأن عندنا فرع فعلي بيقدم تجربة فخمة ومباشرة، وموقع إلكتروني جديد بيقدم تجربة سلسة وسريعة ومعمولة بخطوات محترفة تناسب عميل فاهم ورايق."
          },
          {
            title: "شفافية كاملة",
            text: "لأننا واضحين مع العميل وكل درجة في المنتج مكتوبة بصراحة ومن غير أي تضليل."
          }
        ]
      },
      promise: {
        title: "وعدنا",
        text: "تجربتك هي أولويتنا. جودة حقيقية، خدمة موثوقة، وتجربة شراء تخليك دايماً راجع لـأددميكس."
      }
    }
  };

  const currentContent = content[lang] || content.EN;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <div className={`aboutus-page ${lang === 'AR' ? 'rtl' : 'ltr'}`}>
      {/* Hero Section */}
      <section className="about-us-section" data-section="hero" ref={addToRefs}>
        <div className="about-us-background">
          <div className="liquid-orb orb-1"></div>
          <div className="liquid-orb orb-2"></div>
          <div className="liquid-orb orb-3"></div>
        </div>
        
        <div className="about-us-content">
          {/* <div className="logo-container">
            <img 
              src="/assets/login-form-logo2.png" 
              alt="Addmix Store Logo" 
              className="main-logo"
            />
          </div> */}
          
          <div className="about-us-text">
            <span className="about-us-label">{currentContent.hero.title}</span>
            <h1 className="about-us-title">{currentContent.hero.subtitle}</h1>
            <p className="about-us-tagline">{currentContent.hero.tagline}</p>
            <p className="about-us-description">{currentContent.hero.description}</p>
            <div className="about-us-badge">
              <span>{currentContent.hero.since}</span>
            </div>
          </div>
        </div>

        <div className="scroll-indicator">
          <div className="mouse">
            <div className="wheel"></div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section 
        className={`intro-section ${isVisible.intro ? 'visible' : ''}`}
        data-section="intro"
        ref={addToRefs}
      >
        <div className="glass-card">
          <h2 className="section-title">{currentContent.intro.title}</h2>
          <p className="section-text">{currentContent.intro.text}</p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section 
        className={`vision-mission-section ${isVisible.vision ? 'visible' : ''}`}
        data-section="vision"
        ref={addToRefs}
      >
        <div className="vm-grid">
          <div className="vm-card vision-card">
            <div className="vm-icon">👁️</div>
            <h3>{currentContent.vision.title}</h3>
            <p>{currentContent.vision.text}</p>
          </div>
          
          <div className="vm-card mission-card">
            <div className="vm-icon">🎯</div>
            <h3>{currentContent.mission.title}</h3>
            <p>{currentContent.mission.text}</p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section 
        className={`values-section ${isVisible.values ? 'visible' : ''}`}
        data-section="values"
        ref={addToRefs}
      >
        <h2 className="section-title">{currentContent.values.title}</h2>
        <div className="values-grid">
          {currentContent.values.items.map((value, index) => (
            <div 
              key={index} 
              className="value-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="value-icon">{value.icon}</div>
              <h4>{value.title}</h4>
              <p>{value.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Section */}
      <section 
        className={`why-section ${isVisible.why ? 'visible' : ''}`}
        data-section="why"
        ref={addToRefs}
      >
        <h2 className="section-title">{currentContent.why.title}</h2>
        <div className="why-grid">
          {currentContent.why.reasons.map((reason, index) => (
            <div 
              key={index} 
              className="why-card"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="why-number">{index + 1}</div>
              <h3>{reason.title}</h3>
              <p>{reason.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Promise Section */}
      <section 
        className={`promise-section ${isVisible.promise ? 'visible' : ''}`}
        data-section="promise"
        ref={addToRefs}
      >
        <div className="promise-card">
          <div className="promise-icon">✨</div>
          <h2>{currentContent.promise.title}</h2>
          <p>{currentContent.promise.text}</p>
          <div className="promise-signature">— Addmix Store Team</div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;